import { createFileRoute, Link } from "@tanstack/react-router";
import { STATES_DATA } from "@/data/states";
import { ogImageMeta } from "@/lib/og-image";
import { trackApplyClick } from "@/lib/apply-cta";

const URL = "https://vendingmachinefree.com/free-vending-machines-usa";
const TITLE = "Free Vending Machines USA — All 50 States";
const DESC = "FREE vending machines for US businesses in all 50 states. $0 install, $0 service, $0 repairs. Nationwide operator network — apply in 60 seconds.";

const METROS = ["New York","Los Angeles","Chicago","Houston","Phoenix","Philadelphia","San Antonio","San Diego","Dallas","Austin","Jacksonville","Columbus","Charlotte","Indianapolis","San Francisco","Seattle","Denver","Boston","Nashville","Detroit"];

const FAQS = [
  { q: "Do you provide free vending machines in all 50 states?", a: "Yes. Our operator network covers every US state — from major metros to small towns and industrial parks." },
  { q: "Is there really no cost to the business?", a: "Correct. Machines, install, restocking, cash handling, and repairs are all $0. We earn revenue from product sales." },
  { q: "How do I apply for a free machine in the USA?", a: "Complete the short application on our contact page with your address, employee count, and preferred machine type. We respond in 4–6 hours." },
  { q: "What machines are available nationwide?", a: "Drink, snack, combo, coffee, food, and ice machines — with cashless payment and modern telemetry." },
];

export const Route = createFileRoute("/free-vending-machines-usa")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:site_name", content: "Vending Machine Free.com" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      ...ogImageMeta(),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Free Vending Machines USA",
          provider: { "@type": "Organization", name: "Vending Machine Free.com", url: "https://vendingmachinefree.com", slogan: "Powered by American businesses — get a Free Vending Machine Nationwide." },
          areaServed: { "@type": "Country", name: "United States" },
          serviceType: "Free Vending Machine Placement",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          url: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
            { "@type": "ListItem", position: 2, name: "Free Vending Machines USA", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <section className="bg-steel text-white py-16">
        <div className="container-x">
          <h1 className="font-display text-4xl md:text-5xl uppercase">Free Vending Machines USA — Nationwide Coverage Across All 50 States</h1>
          <p className="mt-4 max-w-3xl text-lg opacity-90">
            We provide FREE vending machines to businesses across the entire United States. From major metros to small towns, our operator network installs, stocks, and services machines at no cost.
          </p>
          <Link to="/" hash="contact" onClick={() => trackApplyClick("free_vending_machines_usa_hero")} className="inline-block mt-6 bg-safety px-6 py-3 font-semibold uppercase">Apply for Free Vending Machines</Link>
        </div>
      </section>

      <section className="py-12">
        <div className="container-x">
          <h2 className="font-display text-2xl uppercase">Nationwide Coverage — All 50 States</h2>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-1 text-sm">
            {STATES_DATA.slice().sort((a,b)=>a.name.localeCompare(b.name)).map(s=>(
              <Link key={s.code} to="/states/$state" params={{state:s.slug}} className="hover:text-safety">{s.name}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-muted">
        <div className="container-x">
          <h2 className="font-display text-2xl uppercase">Major Metros Covered</h2>
          <p className="mt-3">{METROS.join(", ")}, and more.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container-x grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-2xl uppercase">Industries We Serve Nationwide</h2>
            <ul className="mt-4 grid grid-cols-2 gap-1 list-disc pl-5">
              {([
                ["Warehouses","warehouses-manufacturing"],
                ["Offices","offices-corporate"],
                ["Schools","schools-universities"],
                ["Hospitals","hospitals-clinics"],
                ["Hotels","hotels-hospitality"],
                ["Gyms","gyms-fitness-centers"],
                ["Manufacturing","warehouses-manufacturing"],
                ["Logistics","warehouses-manufacturing"],
                ["Airports","airports-aviation"],
                ["Military Bases","military-bases"],
                ["Mines","mines-mining-sites"],
              ] as const).map(([label,slug])=>(
                <li key={label}><Link className="hover:text-safety" to="/industries/$industry" params={{industry:slug}}>{label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl uppercase">Machine Types Available</h2>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {["Drink","Snack","Combo","Coffee","Food","Ice"].map(m=>(
                <Link key={m} to="/vending-machines" className="border p-3 hover:border-safety">{m}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-muted">
        <div className="container-x">
          <h2 className="font-display text-2xl uppercase">Why Businesses Choose Us</h2>
          <ul className="mt-4 grid md:grid-cols-2 gap-2 list-disc pl-5 max-w-3xl">
            <li>$0 install</li><li>$0 service</li><li>$0 repairs</li>
            <li>Modern machines</li><li>Cashless payments</li>
            <li>Fast operator response (4–6 hrs)</li><li>Full nationwide coverage</li>
          </ul>
          <p className="mt-6 text-sm">
            Learn more: <Link to="/free-vending-machine-placement" className="text-safety underline">Free Placement</Link> · <Link to="/vending-machine-placement" className="text-safety underline">Placement Service</Link> · <Link to="/coverage" className="text-safety underline">Coverage Map</Link>
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-2xl uppercase">FAQs</h2>
          <div className="mt-4 space-y-4">
            {FAQS.map(f=>(
              <div key={f.q}><h3 className="font-semibold">{f.q}</h3><p className="text-muted-foreground">{f.a}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-steel text-white py-12 text-center">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">Apply for Free Vending Machines</h2>
          <p className="mt-2 opacity-90">Get FREE vending machines anywhere in the USA — $0 install & full service.</p>
          <Link to="/" hash="contact" onClick={() => trackApplyClick("free_vending_machines_usa_footer")} className="inline-block mt-6 bg-safety px-8 py-3 font-semibold uppercase">Apply Now</Link>
        </div>
      </section>
    </div>
  );
}
